<template>
  <div>
    <md-card>
      <md-card-header class="md-card-header-text md-card-header-green">
        <div class="card-text">
          <h4 class="title text-lg">{{ $t("student.enrollment") }}</h4>
        </div>
      </md-card-header>
      <div class="text-right mr-10">
        <md-button
          v-if="$can(Enrollment.actions.add, Enrollment.subject)"
          class="md-success mr-3"
          @click="addNewEnrollment"
          >{{ $t("common.add_new") }}</md-button
        >
        <md-button
          v-if="tablePagination.length"
          class="md-warning"
          @click="exportPdf"
          >{{ $t("student.export_file") }}</md-button
        >
      </div>
      <md-card-content v-if="tablePagination.length" class="overflow-x-auto">
        <md-table class="min-w-[1024px]">
          <md-table-row>
            <md-table-head>
              {{ $t("student.grade") }}
            </md-table-head>
            <md-table-head>
              {{ $t("student.type") }}
            </md-table-head>
            <md-table-head>
              {{ $t("common.status") }}
            </md-table-head>
            <md-table-head>
              {{ $t("student.calendar") }}
            </md-table-head>
            <md-table-head>
              {{ $t("common.start_date") }}
            </md-table-head>
            <md-table-head>
              {{ $t("common.end_date") }}
            </md-table-head>
            <md-table-head>
              {{ $t("student.action") }}
            </md-table-head>
          </md-table-row>
          <template>
            <md-table-row
              v-for="(item, index) in tablePagination"
              :key="index + 'table'"
              :class="{ 'text-red-600': item.highlight }"
            >
              <md-table-cell> {{ item.grade.name }}</md-table-cell>
              <md-table-cell
                >{{ capitalizeFirstLetter(item.type) }}
              </md-table-cell>
              <md-table-cell>
                {{ capitalizeFirstLetter(item.status) }}
              </md-table-cell>
              <md-table-cell>{{ item.calendar }} </md-table-cell>
              <md-table-cell
                >{{ convertDateFromServer(item.start_date, "date") }}
              </md-table-cell>
              <md-table-cell
                >{{ convertDateFromServer(item.end_date, "date") }}
              </md-table-cell>
              <md-table-cell class="w-10"
                ><md-button
                  v-if="$can(Enrollment.actions.edit, Enrollment.subject)"
                  class="md-just-icon md-warning md-simple table-action"
                  @click.native="editEnrollment(item)"
                >
                  <md-tooltip md-direction="top">
                    {{ $t("common.edit") }}
                  </md-tooltip>
                  <md-icon>edit</md-icon>
                </md-button>
                <md-button
                  v-if="$can(Enrollment.actions.delete, Enrollment.subject)"
                  class="md-just-icon md-danger md-simple table-action ml-2"
                  @click.native="confirmDeleteEnrollment(item._id)"
                >
                  <md-tooltip md-direction="top">
                    {{ $t("common.delete") }}
                  </md-tooltip>
                  <md-icon>close</md-icon>
                </md-button>
              </md-table-cell>
            </md-table-row>
          </template>
        </md-table>
        <yv-pagination
          @changePage="changePage"
          :pagination="pagination"
        ></yv-pagination>
      </md-card-content>
      <md-card-content v-else class="min-h-[300px]">
        <no-data-found></no-data-found>
      </md-card-content>
    </md-card>
  </div>
</template>
<script>
import { service_names } from "@/constants/Services";
import Swal from "sweetalert2";
import YvPagination from "@/components/Base/YvPagination";
import pick from "lodash/pick";
import NoDataFound from "@/components/Base/Illustration/NoDataFound";
import { Enrollment } from "@/constants/Permissions";
import { capitalizeFirstLetter, Message } from "@/helpers/utils";
import { convertDateFromServer } from "@/helpers/datetime-helper";

export default {
  components: { NoDataFound, YvPagination },
  props: ["isActiveTab"],
  data() {
    return {
      studentId: this.$route.params.id,
      modalAddName: "add-new-enrollment",
      componentKey: 0,
      defaultTableData: [],
      tablePagination: [],
      defaultStartPage: 1,
      pagination: {
        currentPage: 1,
        perPage: 10,
        from: 0,
        to: 0,
        total: 0
      },
      capitalizeFirstLetter,
      Enrollment,
      convertDateFromServer,
      isActive: false
    };
  },
  watch: {
    isActiveTab: function(newVal) {
      if (this.isActive) return;
      if (newVal) {
        this.fetchEnrollment();
        this.isActive = true;
      }
    }
  },
  methods: {
    changePage(value) {
      this.pagination.currentPage = value;
      let start = (value - 1) * this.pagination.perPage;
      this.pagination.from = start + 1;
      this.pagination.to = start + this.pagination.perPage;
      this.tablePagination = this.defaultTableData.slice(
        start,
        start + this.pagination.perPage
      );
    },
    exportPdf() {
      const path = `${service_names.utility}/export/enrollment/student/${this.studentId}`;
      this.$axios.get(path, {}, true, {}, "blob").then(response => {
        if (response.status === 200) {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "enrollment.pdf");
          document.body.appendChild(link);
          link.click();
          Message.success(this.$t("common.export_success"));
          link.remove();
        } else {
          Message.error(this.$t("common.an_error_occurred"));
        }
      });
    },
    fetchEnrollment() {
      this.$axios
        .get(`${service_names.user}/enrollment`, {
          student_id: this.studentId,
          order_by: "grade_id DESC"
        })
        .then(response => {
          this.defaultTableData = response.data;
          this.pagination.total = this.defaultTableData.length;
          this.changePage(this.defaultStartPage);
        });
    },
    addNewEnrollment() {
      this.$emit("add-enrollment");
    },
    editEnrollment(item) {
      this.$emit("edit-enrollment", item);
    },
    confirmDeleteEnrollment(id) {
      Swal.fire({
        title: this.$t("common.are_you_sure"),
        text: this.$t("common.you_wont_be_able_to_revert_this"),
        type: "warning",
        showCancelButton: true,
        confirmButtonClass: "md-button md-success btn-fill",
        cancelButtonClass: "md-button md-danger btn-fill",
        confirmButtonText: this.$t("common.yes_delete_it"),
        buttonsStyling: false
      }).then(result => {
        if (result.value) {
          this.deleteEnrollment(id);
        }
      });
    },
    deleteEnrollment(id) {
      this.$axios.delete(`${service_names.user}/enrollment/${id}`).then(() => {
        Message.success(this.$t("common.success"));
        this.fetchEnrollment();
      });
    }
  },
  provide() {
    return {
      editEnrollment: this.editEnrollment,
      confirmDeleteEnrollment: this.confirmDeleteEnrollment
    };
  }
};
</script>
<style lang="scss" scoped></style>
